/**
 * Which agent a golden test case runs on, and the domain-tag helpers the editor
 * needs to keep a tag's agent across an edit.
 *
 * Mirrors the EvaluationManager's choice (golden_selection.assign_case_agents):
 * an explicit `agent_name`, else the case's first skill tag agent (agentless
 * legacy tags count as rocky), else the first domain tag's `agent`, else the
 * run's default, rocky.
 */

export const DEFAULT_AGENT = "rocky";

// Agents that own skills / can run golden cases. Kept in sync with the other
// eval pages' local lists.
export const SKILL_TAG_AGENTS = ["rocky", "acquisition_agent", "campaign_copilot_agent", "data_ops_agent", "marketing_agent"];

export interface SkillTag {
  agent?: string;
  skill: string;
}

// A domain tag is either a bare domain name (whole domain) or a structured
// slice {domain, sub_domain}; `agent` names the agent the case runs on.
export interface DomainTag {
  domain: string;
  sub_domain?: string;
  agent?: string;
}

export interface AgentTaggedCase {
  agent_name?: string | null;
  skills_tag?: (string | SkillTag)[];
  domains_tag?: (string | DomainTag)[];
}

export interface EffectiveAgent {
  agent: string;
  /** True when nothing on the case names an agent: the run's default applies. */
  isDefault: boolean;
}

export function effectiveAgent(c: AgentTaggedCase): EffectiveAgent {
  const explicit = c.agent_name?.trim();
  if (explicit) return { agent: explicit, isDefault: false };

  for (const tag of c.skills_tag || []) {
    if (typeof tag === "string") {
      if (tag.trim()) return { agent: DEFAULT_AGENT, isDefault: false };
    } else if (tag?.skill) {
      return { agent: tag.agent || DEFAULT_AGENT, isDefault: false };
    }
  }

  for (const tag of c.domains_tag || []) {
    if (typeof tag !== "string" && tag?.agent?.trim()) return { agent: tag.agent.trim(), isDefault: false };
  }
  return { agent: DEFAULT_AGENT, isDefault: true };
}

/** The cases of the eval pages' current agent (the agent selected outside this page). */
export function filterByAgent<T extends AgentTaggedCase>(cases: T[], agent: string): T[] {
  return cases.filter((c) => effectiveAgent(c).agent === agent);
}

/**
 * Skill Agent for the editor. A case's skill tags name their own agent; without
 * tags the field stores nothing, so it shows the agent the case runs on (else the
 * current agent) instead of a misleading rocky.
 */
export function skillAgentFor(c: AgentTaggedCase | null, currentAgent: string): string {
  const tagged = (c?.skills_tag || []).find((t) => typeof t !== "string" && t?.skill);
  if (tagged && typeof tagged !== "string") return tagged.agent || DEFAULT_AGENT;
  if ((c?.skills_tag || []).some((t) => typeof t === "string" && t.trim())) return DEFAULT_AGENT;
  const fallback = c ? effectiveAgent(c).agent : currentAgent;
  return SKILL_TAG_AGENTS.includes(fallback) ? fallback : DEFAULT_AGENT;
}

// Text form <-> stored form. Text uses "domain/sub_domain" (mirrors the
// backend's canonical domain_tag_key); a bare "domain" is a whole-domain tag.
export function domainTagToText(tag: string | DomainTag): string {
  if (typeof tag === "string") return tag;
  return tag.sub_domain ? `${tag.domain}/${tag.sub_domain}` : tag.domain;
}

export function parseDomainTag(text: string): string | DomainTag {
  const slash = text.indexOf("/");
  if (slash === -1) return text; // bare domain — whole-domain tag
  const domain = text.slice(0, slash).trim();
  const sub = text.slice(slash + 1).trim();
  return sub ? { domain, sub_domain: sub } : domain;
}

/**
 * Re-attach the `agent` of the original domain tags to the edited ones. The
 * editor shows tags as "domain[/sub_domain]" text, which has no room for the
 * agent; a tag whose text the user left unchanged keeps its agent on save.
 */
export function keepDomainTagAgents(
  edited: (string | DomainTag)[],
  original: (string | DomainTag)[] | undefined,
): (string | DomainTag)[] {
  const agents = new Map<string, string>();
  for (const tag of original || []) {
    if (typeof tag !== "string" && tag.agent) agents.set(domainTagToText(tag), tag.agent);
  }
  return edited.map((tag) => {
    const agent = agents.get(domainTagToText(tag));
    if (!agent) return tag;
    return typeof tag === "string" ? { domain: tag, agent } : { ...tag, agent };
  });
}
